import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { useNotify } from 'react-admin';
import { dayjs } from '../../../components/ex_dayjs';
import MAutoComplete from '../../../components/controls/MAutoComplete';
import MDialog from '../../../components/controls/MDialog';
import MCheckbox from '../../../components/controls/MCheckbox';
import MDatePicker from '../../../components/controls/MDatePicker';
import MDateRangePicker from '../../../components/controls/MDateRangePicker';
import MNumber from '../../../components/controls/MNumber';
import { getRaStore, setRaStore } from '../../../utils/common';

/**
 * Configuration object for re-filter settings
 */
interface PreFilterConfig {
    dateFrom: string | null;
    excludedCategories: string[];
    excludeActiveInventory: boolean;
    reStock: boolean;
    reStockStartDate: string | null;
    reStockEndDate: string | null;
    seatsRemainPercent: number;
    maxPrimaryPrice: number;
    oldest_available_seats: number;
}

/**
 * Filtering rule structure
 */
interface FilteringRule {
    variable: string;
    condition: string;
    value: any;
}

/**
 * Category choice object structure
 */
export interface CategoryChoice {
    name: string;
    id: string | number;
}

/**
 * Props for the PreFilterConfigDialog component
 */
interface PreFilterConfigDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Callback when dialog is closed */
    onClose: () => void;
    /** Array of pre-filtering rules */
    preFilteringRules: FilteringRule[];
    /** Callback when configuration is saved with new rules */
    onSave: (rules: FilteringRule[]) => void;
    /** Function to get categories with type information */
    getCategoriesWithType?: () => CategoryChoice[];
}
        
// Default configuration values
const defaultFilterConfig: PreFilterConfig = {
    dateFrom: null,
    excludedCategories: [],
    excludeActiveInventory: true,
    reStock: false,
    reStockStartDate: null,
    reStockEndDate: null,
    seatsRemainPercent: 50,
    maxPrimaryPrice: 100,
    oldest_available_seats: 0
};

// LocalStorage key for restock date range persistence
const RESTOCK_DATERANGE_KEY = 'refilter_restock_daterange';

/**
 * PreFilterConfigDialog
 * Dialog component for configuring pre-filter settings including date ranges,
 * category exclusions, and inventory options. Integrates with preFilteringRules state.
 */
const PreFilterConfigDialog: React.FC<PreFilterConfigDialogProps> = ({
    open,
    onClose,
    preFilteringRules,
    onSave,
    getCategoriesWithType
}) => {
    const notify = useNotify();
    const [preFilterConfig, setPreFilterConfig] = useState<Partial<PreFilterConfig>>({});

    /**
     * Populate configuration from preFilteringRules when dialog opens
     */
    useEffect(() => {
        if (open && preFilteringRules && preFilteringRules.length > 0) {
            let currentConfig: Partial<PreFilterConfig> = {};

            // Extract date rule
            const dateRule = preFilteringRules.find(rule => rule.variable === 'time_left_days');
            currentConfig.dateFrom = dateRule?.value !== null && dateRule?.value !== undefined ? dayjs().startOf('day').add(dateRule.value, 'day').toISOString() : null;

            // Check localStorage first for stored date range values
            const storedDateRange = getRaStore(RESTOCK_DATERANGE_KEY);
            if (storedDateRange) {
                currentConfig.reStockStartDate = storedDateRange.reStockStartDate;
                currentConfig.reStockEndDate = storedDateRange.reStockEndDate;
            } else {
                // Fall back to calculating from re_stock_in_days rule
                const restockInDaysRule = preFilteringRules.find(rule => rule.variable === 're_stock_in_days');
                if (restockInDaysRule) {
                    currentConfig.reStockStartDate = dayjs().startOf('day').subtract(restockInDaysRule.value, 'day').toISOString();
                    currentConfig.reStockEndDate = dayjs().endOf('day').toISOString();
                }
            }
            // Extract category exclusion rule
            const categoryRule = preFilteringRules.find(rule => rule.variable === 'eventCategoryName' && rule.condition === 'not_in_list');
            if (categoryRule && categoryRule.value) {
                currentConfig.excludedCategories = categoryRule.value;
            }

            // Extract active inventory rule
            const activeInventoryRule = preFilteringRules.find(rule => rule.variable === 'exclude_active_inventory');
            if (activeInventoryRule) {
                currentConfig.excludeActiveInventory = activeInventoryRule.condition === 'equals' && activeInventoryRule.value === true;
            }

            // Extract re-stock rule
            const reStockRule = preFilteringRules.find(rule => rule.variable === 're_stock');
            if (reStockRule) {
                currentConfig.reStock = reStockRule.condition === 'equals' && reStockRule.value === true;
            }

            // Extract seats remain percent rule
            const seatsRemainRule = preFilteringRules.find(rule => rule.variable === 'seats_remain_percent');
            if (seatsRemainRule) {
                currentConfig.seatsRemainPercent = seatsRemainRule.value;
            }

            // Extract max primary price rule
            const maxPrimaryPriceRule = preFilteringRules.find(rule => rule.variable === 'maxPrimaryPrice');
            if (maxPrimaryPriceRule) {
                currentConfig.maxPrimaryPrice = maxPrimaryPriceRule.value;
            }
            // Extract oldest available seats rule
            const oldestAvailableSeatsRule = preFilteringRules.find(rule => rule.variable === 'oldest_available_seats');
            if (oldestAvailableSeatsRule) {
                currentConfig.oldest_available_seats = oldestAvailableSeatsRule.value;
            }

            setPreFilterConfig(currentConfig);
        } else if (open) {
            // Reset to default values when opening with no existing rules
            setPreFilterConfig(defaultFilterConfig);
        }
    }, [open, preFilteringRules]);

    /**
     * Handle save configuration
     */
    const handleSave = (): void => {
        const newRules: FilteringRule[] = [];

        // Add date rule if dateFrom is specified
        const daysFromNow = preFilterConfig.dateFrom ? dayjs(preFilterConfig.dateFrom).startOf('day').diff(dayjs().startOf('day'), 'day') : null;
        newRules.push({
            variable: 'time_left_days',
            condition: 'less_than_or_equal',
            value: daysFromNow
        });

        // Add excluded categories rule
        newRules.push({
            variable: 'eventCategoryName',
            condition: 'not_in_list',
            value: preFilterConfig.excludedCategories || []
        });

        // Add active inventory exclusion rule
        newRules.push({
            variable: 'exclude_active_inventory',
            condition: 'equals',
            value: preFilterConfig.excludeActiveInventory ?? true
        });

        // Add re-stock rule
        newRules.push({
            variable: 're_stock',
            condition: 'equals',
            value: preFilterConfig.reStock ?? false
        });
        // Add re-stock range rule if reStock is enabled and start date is specified
        if (preFilterConfig.reStock) {
            newRules.push({
                variable: 're_stock_from_date',
                condition: 'greater_than_or_equal',
                value: preFilterConfig.reStockStartDate || null
            });
            newRules.push({
                variable: 're_stock_to_date',
                condition: 'less_than_or_equal',
                value: preFilterConfig.reStockEndDate || null
            });

            // Persist restock date range to localStorage
            setRaStore(RESTOCK_DATERANGE_KEY, {
                reStockStartDate: preFilterConfig.reStockStartDate,
                reStockEndDate: preFilterConfig.reStockEndDate
            });
        }

        // Add seats remain percent rule if specified
        if (preFilterConfig.seatsRemainPercent !== null && preFilterConfig.seatsRemainPercent !== undefined && preFilterConfig.seatsRemainPercent >= -1) {
            newRules.push({
                variable: 'seats_remain_percent',
                condition: 'less_than_or_equal',
                value: preFilterConfig.seatsRemainPercent
            });
        }

        // Add max primary price rule if specified
        if (preFilterConfig.maxPrimaryPrice !== null && preFilterConfig.maxPrimaryPrice !== undefined && preFilterConfig.maxPrimaryPrice > 0) {
            newRules.push({
                variable: 'maxPrimaryPrice',
                condition: 'less_than_or_equal',
                value: preFilterConfig.maxPrimaryPrice
            });
        }

        // Add oldest available seats rule if specified
        if (preFilterConfig.oldest_available_seats !== null && preFilterConfig.oldest_available_seats !== undefined && preFilterConfig.oldest_available_seats >= 0) {
            newRules.push({
                variable: 'oldest_available_seats',
                condition: 'greater_than_or_equal',
                value: preFilterConfig.oldest_available_seats
            });
        }

        // Call parent save handler with new rules
        onSave(newRules);

        // Close dialog
        onClose();

        // Show success notification
        notify('Re-filter configuration saved successfully', { type: 'success' });
    };

    /**
     * Handle cancel - just close dialog without saving
     */
    const handleCancel = (): void => {
        onClose();
    };

    return (
        <MDialog
            open={open}
            handleClose={handleCancel}
            title="Pre-filter Configuration"
            name="Pre-filter-config"
            maxWidth="md"
            action={
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                >
                    Save
                </Button>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <MDatePicker
                    label="Date From"
                    value={preFilterConfig.dateFrom}
                    onChange={(newValue: string | null) => setPreFilterConfig(prev => ({ ...prev, dateFrom: newValue }))}
                />
                <MAutoComplete
                    label="Exclude category list"
                    choices={getCategoriesWithType ? getCategoriesWithType() : []}
                    onChange={(values: CategoryChoice[] | string[]) => {
                        const categoryNames: string[] = values
                            ? values.map((ctg: CategoryChoice | string) => typeof ctg === 'string' ? ctg : ctg.name)
                            : [];
                        setPreFilterConfig(prev => ({ ...prev, excludedCategories: categoryNames }));
                    }}
                    value={preFilterConfig.excludedCategories?.map(cat => ({ name: cat, id: cat })) || []}
                    variant="standard"
                    isMulti={true}
                />
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'center' }}>
                    <MCheckbox
                        label="Exclude active inventory"
                        defaultValue={preFilterConfig.excludeActiveInventory}
                        onChange={(checked: boolean) => setPreFilterConfig(prev => ({ ...prev, excludeActiveInventory: checked }))}
                        key={`exclude-inventory-${preFilterConfig.excludeActiveInventory}`}
                    />
                    <MNumber
                        label="% seats remain"
                        sx={{ width: '15%', m: 1 }}
                        value={preFilterConfig.seatsRemainPercent}
                        onChange={(newValue: number) => setPreFilterConfig(prev => ({ ...prev, seatsRemainPercent: newValue }))}
                        decimal={2}
                        range={[-1, 100]}
                        isNegative={false}
                        size="small"
                        tooltip="Input 0 to disable this filter"
                    />
                    <MNumber
                        label="Max Primary Price"
                        sx={{ width: '15%', m: 1 }}
                        value={preFilterConfig.maxPrimaryPrice}
                        onChange={(newValue: number) => setPreFilterConfig(prev => ({ ...prev, maxPrimaryPrice: newValue }))}
                        decimal={0}
                        range={[0, 10000]}
                        isNegative={false}
                        size="small"
                        tooltip="Filters seat recommendations after event scoring (does not affect event search)"
                    />
                    <MNumber
                        label="Min Venue Capacity"
                        sx={{ width: '15%', m: 1 }}
                        value={preFilterConfig.oldest_available_seats}
                        onChange={(newValue: number) => setPreFilterConfig(prev => ({ ...prev, oldest_available_seats: newValue }))}
                        decimal={0}
                        range={[0, 10000]}
                        isNegative={false}
                        size="small"
                        tooltip="Minimum venue capacity to consider for pre-filtering"
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    <MCheckbox
                        defaultValue={preFilterConfig.reStock}
                        onChange={(checked: boolean) => setPreFilterConfig(prev => ({ ...prev, reStock: checked }))}
                        key={`re-stock-${preFilterConfig.reStock}`}
                    />
                    <MDateRangePicker
                        disabled={!preFilterConfig.reStock}
                        label="Re-stock from"
                        defaultValue={[preFilterConfig.reStockStartDate, preFilterConfig.reStockEndDate]}
                        name="reStockDateRange"
                        key={'restock-date-range'}
                        isUTC={true}
                        onChange={(value: string | null, order: string) => {
                            if (order.includes('_ge')) {
                                setPreFilterConfig(prev => ({ ...prev, reStockStartDate: value }));
                            } else {
                                setPreFilterConfig(prev => ({ ...prev, reStockEndDate: value }));
                            }
                        }}

                    />
                </Box>
            </Box>
        </MDialog>
    );
};

export default PreFilterConfigDialog;
